import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Chip,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Fleet } from '../../types';

interface FleetReservationSettingsProps {
  fleet: Fleet;
  onSave: (settings: {
    publicLinkEnabled: boolean;
    requireEmailValidation: boolean;
    allowedEmailDomains: string[];
  }) => Promise<void>;
  onGenerateLink?: (fleetId: string) => Promise<void>;
}

const FleetReservationSettings: React.FC<FleetReservationSettingsProps> = ({ 
  fleet, 
  onSave,
  onGenerateLink 
}) => {
  const { t } = useTranslation();
  const [publicLinkEnabled, setPublicLinkEnabled] = useState(fleet.publicLinkEnabled || false);
  const [requireEmailValidation, setRequireEmailValidation] = useState(fleet.requireEmailValidation || false);
  const [allowedDomains, setAllowedDomains] = useState<string[]>(fleet.allowedEmailDomains || []);
  const [newDomain, setNewDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAddDomain = () => {
    if (!newDomain) return;
    
    // Validation simple du format de domaine
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain)) {
      setError(t('fleet.settings.invalidDomainFormat', 'Format de domaine invalide'));
      return;
    }
    
    if (allowedDomains.includes(newDomain)) {
      setError(t('fleet.settings.domainAlreadyExists', 'Ce domaine est déjà dans la liste'));
      return;
    }
    
    setAllowedDomains([...allowedDomains, newDomain]);
    setNewDomain('');
    setError(null);
  };

  const handleRemoveDomain = (domain: string) => {
    setAllowedDomains(allowedDomains.filter(d => d !== domain));
  };

  const handleSaveSettings = async () => {
    try {
      await onSave({
        publicLinkEnabled,
        requireEmailValidation,
        allowedEmailDomains: allowedDomains
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('fleet.settings.reservationSettings', 'Paramètres de réservation')}
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={publicLinkEnabled}
              onChange={(e) => setPublicLinkEnabled(e.target.checked)}
            />
          }
          label={t('fleet.settings.enablePublicLink', 'Activer le lien de réservation public')}
        />
        
        {publicLinkEnabled && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('fleet.settings.publicLinkUrl', 'URL du lien public')}:
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={fleet.publicLinkId ? `${window.location.origin}/reserve/${fleet.publicLinkId}` : t('fleet.settings.linkNotGenerated', 'Lien non généré')}
              InputProps={{
                readOnly: true,
              }}
            />
            <Button 
              variant="outlined" 
              size="small" 
              sx={{ mt: 1 }}
              onClick={() => onGenerateLink && onGenerateLink(fleet.id)}
            >
              {fleet.publicLinkId ? t('fleet.settings.regenerateLink', 'Régénérer le lien') : t('fleet.settings.generateLink', 'Générer un lien')}
            </Button>
          </Box>
        )}
      </Paper>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('fleet.settings.accessRestrictions', 'Restrictions d\'accès')}
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={requireEmailValidation}
              onChange={(e) => setRequireEmailValidation(e.target.checked)}
            />
          }
          label={t('fleet.settings.requireEmailValidation', 'Exiger une validation par email')}
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          {t('fleet.settings.allowedEmailDomains', 'Domaines email autorisés')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('fleet.settings.allowedEmailDomainsHelp', 'Seuls les utilisateurs avec une adresse email de ces domaines pourront réserver des véhicules')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', mb: 2 }}>
          <TextField
            label={t('fleet.settings.addDomain', 'Ajouter un domaine')}
            placeholder="entreprise.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            size="small"
            fullWidth
          />
          <Button 
            variant="contained" 
            onClick={handleAddDomain}
            sx={{ ml: 1 }}
          >
            {t('reservation:common.add', 'Ajouter')}
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {allowedDomains.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('fleet.settings.noDomains', 'Aucun domaine configuré. Tous les emails seront acceptés.')}
            </Typography>
          ) : (
            allowedDomains.map((domain) => (
              <Chip
                key={domain}
                label={domain}
                onDelete={() => handleRemoveDomain(domain)}
                color="primary"
                variant="outlined"
              />
            ))
          )}
        </Box>
      </Paper>
      
      <Button 
        variant="contained" 
        color="primary"
        onClick={handleSaveSettings}
      >
        {t('reservation:common.save', 'Enregistrer')}
      </Button>
    </Box>
  );
};

export default FleetReservationSettings;
